import type { GuildWarRosterBoardRecord } from "@/lib/guild-war-api"
import type { TeamBoardState } from "@/lib/team-board-store"

export type SaveStatus = "idle" | "saving" | "saved" | "error" | "conflict"

export type DraftSnapshotInput = {
  board: TeamBoardState
  sourceWeekId: string
  displayDate: string
}

export type PublishPreparationResult =
  | { action: "wait-for-save" }
  | { action: "save-before-publish"; expectedVersion: number }
  | { action: "publish" }

export function createDraftSnapshot(input: DraftSnapshotInput) {
  return JSON.stringify(input)
}

export function createRosterCoordinatorState(input?: {
  currentVersion?: number
  lastSavedSnapshot?: string | null
}) {
  return {
    currentVersion: input?.currentVersion ?? 0,
    lastSavedSnapshot: input?.lastSavedSnapshot ?? null,
    saveStatus: "idle" as SaveStatus,
    saveMessage: null as string | null,
    hasPendingSave: false,
  }
}

export function beginDraftSave(
  state: ReturnType<typeof createRosterCoordinatorState>,
) {
  return {
    ...state,
    hasPendingSave: true,
    saveStatus: "saving" as SaveStatus,
    saveMessage: "Saving draft...",
  }
}

export function applySaveSuccess(
  state: ReturnType<typeof createRosterCoordinatorState>,
  input: {
    record: GuildWarRosterBoardRecord
    snapshot: string
    successMessage?: string | null
  },
) {
  return {
    ...state,
    currentVersion: input.record.version,
    lastSavedSnapshot: input.snapshot,
    hasPendingSave: false,
    saveStatus: "saved" as SaveStatus,
    saveMessage:
      input.successMessage === undefined
        ? `Saved v${input.record.version}`
        : input.successMessage,
  }
}

export function applySaveFailure(
  state: ReturnType<typeof createRosterCoordinatorState>,
  input: {
    code?: string
    message: string
  },
) {
  return {
    ...state,
    hasPendingSave: false,
    saveStatus:
      input.code === "ROSTER_VERSION_CONFLICT"
        ? ("conflict" as SaveStatus)
        : ("error" as SaveStatus),
    saveMessage:
      input.code === "ROSTER_VERSION_CONFLICT"
        ? "Draft changed on the server. Reload the week to continue."
        : input.message,
  }
}

export function resolvePublishPreparation(
  state: ReturnType<typeof createRosterCoordinatorState>,
  currentSnapshot: string,
): PublishPreparationResult {
  if (state.hasPendingSave) {
    return { action: "wait-for-save" }
  }

  if (currentSnapshot !== state.lastSavedSnapshot) {
    return {
      action: "save-before-publish",
      expectedVersion: state.currentVersion,
    }
  }

  return { action: "publish" }
}
